import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { CartService } from './cart.service';
import { BundleEditionType, FireBaseProductOrder, OrderResponse, ProductOrder, ProductOrderToSave, UpdateOrderParams } from './types/order.type';
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from './constants/databases';
import { FirebaseCrudService } from './firebase-crud.service';
import { Session } from './types/session.type';
import { ApiService } from './api.service';
import { RequestHandlerParams } from './types/api-service.types';
import { ApiResponse } from './common/types';
import { filter, finalize, from, map, Observable, of, tap, throwError } from 'rxjs';
import { NotificationService } from './notification/notification.service';
import { DeliveriesService } from '../settings/account/deliveries/deliveries.service';
import { StockService } from '../stock/stock.service';
import { ModalContentService } from './modal-content/modal-content.service';
import { ModalContentTypes } from './constants/modal-content-types';
import { arrayToMap, arrayToMapMultiKey, mapToArray, openModalTanksForSubmit } from './common/utils';
import { DateTime } from 'luxon';
import { environment } from '../../environments/environment';
import { MarketStatus } from './types/common.types';
import { KlaviyoService } from './klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from './klaviyo/events';
import { eCommercePermissions } from './types/account.types';
import { formatDateToReadableString } from './utils/formatting';
import { PreOrderData, PreOrderedProduct, PreOrderPayloadProduct, Product } from '../product/product.types';
import { CalculationsService } from './calculations.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private apiService = inject(ApiService);
  private firebaseCrudService = inject(FirebaseCrudService);
  private localStorageService = inject(LocalStorageService);
  private notificationService = inject(NotificationService);
  private deliveriesService = inject(DeliveriesService);
  private stockService = inject(StockService);
  private modalContentService = inject(ModalContentService);
  private klaviyoService = inject(KlaviyoService);
  #calculationsService = inject(CalculationsService);

  endpoints = {
    order: `/order`,
    coupon: '/account/coupons',
    ecommerceLogs: '/ecommerce-logs',
    preOrder: '/pre-order',
  };

  odooOrder: WritableSignal<OrderResponse | any> = signal(null)
  notSavedOrder: WritableSignal<any> = signal(null)
  order: WritableSignal<any> = signal(null)
  productOrder: WritableSignal<ProductOrder | any> = signal(null)

  openModalOrderNotSubmitted: WritableSignal<boolean> = signal(false);
  hasProductInCart: WritableSignal<any> = signal(false)

  notSavedCartEventCreated: WritableSignal<boolean> = signal(false);

  showCouponErrorMessage: WritableSignal<boolean> = signal(false);

  couponErrorMessage: WritableSignal<string> = signal('');

  isLoadedOdooOrder: WritableSignal<boolean> = signal(false);

  constructor() { }

  getOrder(marketStatus?: WritableSignal<MarketStatus>) {

    this.isLoadedOdooOrder.set(false);
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const permissions: eCommercePermissions | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PERMISSIONS);
    if (!sessionStored || (!sessionStored.settings?.hasPaymentMethod && permissions?.settings.paymentMethod.allowed)) return;

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order,
      method: 'GET',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        this.isLoadedOdooOrder.set(true);
        if (!response)
          throw new Error('getOrder Error');

        const order = response?.data;
        this.odooOrder.set(order)
        if (marketStatus && environment.config.flows.shop.validateMarketState && order.marketStatus)
          marketStatus.set(order.marketStatus);
      }),
      finalize(() => this.isLoadedOdooOrder.set(true)),
    ).subscribe();
  }

  editSubKeys(payload: any, firebaseOrder: any): void | null {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (!sessionStored?.accountInfo?.id) return

    let { updateData } = payload;

    // Validate if firebase order will be empty:
    let hasData = false;
    for (const key of Object.keys(updateData)) {
      if (updateData[key].length) {
        hasData = true;
        break;
      } else {
        if (key.includes('common')) {
          if (firebaseOrder?.products?.subscription?.length) {
            hasData = true;
            break
          }
        } else {
          if (firebaseOrder?.products?.common?.length) {
            hasData = true;
            break
          }
        }
      }
    }

    if (hasData) {
      // Setup cutoff date:
      updateData = { ...updateData, ...this.setUpDateForFirebase() }
    } else {
      // Clear cutoff date:
      updateData = { ...updateData, cutoffDate: null }
    }

    const firebasePayload = {
      collection: FIREBASE_COLLECTIONS.ORDERS,
      docId: sessionStored.accountInfo.id.toString(),
      updateData
    }

    this.firebaseCrudService.updateSubkeys(firebasePayload);
  }

  editSubKey(payload: any, editCutOffKey: boolean = true, clearCutoff: boolean = false, clearEventCreated: boolean = false) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (!sessionStored?.accountInfo?.id) return

    const { subKeyPath, newValue } = payload;
    let updateData: any = {};
    updateData[subKeyPath] = newValue;

    // Setup cutoff date:
    if (editCutOffKey) {
      if (clearCutoff)
        updateData = { ...updateData, cutoffDate: null, lastUpdate: null, isNotYetSavedEventGenerated: null }
      else
        updateData = { ...updateData, ...this.setUpDateForFirebase() }
    }

    if (clearEventCreated) {
      updateData = { ...updateData, eventCreated: false }
    }

    const eventCreated = this.notSavedCartEventCreated();

    // If the firebase order does not have "eventCreate" as "true", it should be sent as "false":
    if (!eventCreated) {
      updateData = { ...updateData, eventCreated: false }
    }

    const firebasePayload = {
      collection: FIREBASE_COLLECTIONS.ORDERS,
      docId: sessionStored.accountInfo.id.toString(),
      updateData
    }

    return this.firebaseCrudService.updateSubkeys(firebasePayload);

  }

  setUpDateForFirebase() {
    const deliveryInfo = this.deliveriesService.deliveryZoneInfo();
    if (!deliveryInfo) return {};

    let cutoffObject = { date: deliveryInfo.cutoffDate, time: deliveryInfo.cutoffTime }
    if (deliveryInfo.order) {
      cutoffObject.date = deliveryInfo.order.cutoffDate;
      cutoffObject.time = deliveryInfo.order.cutoffTime;
    }

    // Combina las dos cadenas en una sola
    const dateTimeStr = `${cutoffObject.date} ${cutoffObject.time}`;

    // Extraer partes de la fecha y la hora
    const [datePart, time, period] = dateTimeStr.split(/[\s]+/);
    const [hours, minutes] = time.split(':').map(Number);
    let adjustedHours = hours;

    if (period.toLowerCase() === "p.m." && hours !== 12) {
      adjustedHours = hours + 12;
    } else if (period.toLowerCase() === "a.m." && hours === 12) {
      adjustedHours = 0;
    }

    // Crear una cadena en formato ISO y convertirla a UTC
    const formattedDateTimeStr = `${datePart}T${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`;

    const utcDate = DateTime.fromISO(formattedDateTimeStr, { zone: 'UTC' });

    // Convertir a milisegundos desde epoch
    const utcMillis = utcDate.toMillis();

    return { cutoffDate: utcMillis, lastUpdate: Date.now(), isNotYetSavedEventGenerated: false };
  }

  updateOrder(data: UpdateOrderParams, isSubscription: boolean, showDefaultMessage = true): Observable<ApiResponse<any>> {

    try {
      this.#disableCouponErrorMessage();

      const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
      if (!sessionStored || !Object.keys(sessionStored).length)
        throw new Error("ERROR: SESSION NOT FOUNDED");

      const product = this.setUpOrderProducts(data.product as any, isSubscription);

      const bodyRequest: any = {
        products: [product],
        bundleChangesLog: data.bundleChangesLog || null,
      };

      const method = data.orderId ? 'PATCH' : 'POST';

      if (!data.orderId)
        bodyRequest.deliveryDate = data.deliveryDate;

      const params: RequestHandlerParams = {
        endpoint: data.orderId ? `${this.endpoints.order}/${data.orderId}` : this.endpoints.order,
        method,
        body: bodyRequest,
        apiV3: true,
        returnError: true,
        showErrorMessage: false
      }

      return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
        filter(response => {
          if (!response.error) return true;

          // const c = coupon ? coupon() : null;
          // if (c)
          //   this.#enableCouponErrorMessage(response.message);

          if (response.status === 400)
            this.#updateOrderErrorResponseHandler(response.data, response.message, sessionStored);
          else
            this.notificationService.show({ text: response.message || 'An error occurred while trying to save the order information.', type: 'error' });
          return false;
        }),
        tap((response: any) => {
          this.updateOrderResponseHandler(response, showDefaultMessage);
        }),
      )

    } catch (error) {
      return of<ApiResponse<any>>();
    }
  }

  #updateOrderErrorResponseHandler(data: any, message: any, sessionStored: Session) {

    if (!data?.outOfStockProducts?.length)
      return this.notificationService.show({ text: message || 'An error occurred while trying to save the order information.', type: 'error' });

    const oosProducts: { name: string, variantName: string, variantId: number, isSubscription: boolean, bundleId: number, productExists: boolean, reason: string, previousQuantity: number }[] = data.outOfStockProducts;
    this.validateOutOfStockResponse(oosProducts, true);
  }

  /**
   * Generates order as an HTML table from the given JSON object for log registration.
   *
   * @param {Object} jsonData - The JSON object containing payment details and products.
   * @returns {string} - The generated HTML table as a string.
   */
  private generateOrderHtmlTableFromJson(jsonData: any): string {
    const { paymentDetails, products } = jsonData;

    // Start building the HTML table
    let html = '<table class="table table-bordered o_table">';

    // Add payment details
    html += '<tr><th colspan="2" style="font-weight: bolder; text-align: center">Payment Details</th></tr>';
    for (const [key, value] of Object.entries(paymentDetails)) {
      if (typeof value === 'object' && value !== null) {
        html += `<tr><td style="text-transform:capitalize">${key}</td><td>${paymentDetails[key].isRecurrent ? 'Recurrent:' : ''} <b>$ ${paymentDetails[key].amount}</b></td></tr>`;
      } else {
        html += `<tr><td style="text-transform:capitalize">${key}</td><td>${value || ''}</td></tr>`;
      }
    }

    // Add product details
    html += '<tr><th colspan="2" style="font-weight: bolder; text-align: center">Products</th></tr>';
    products.forEach((product: any, index: number) => {
      html += `<tr><td colspan="2" style="font-weight: bolder">${product.name}</td></tr>`;
      for (const [key, value] of Object.entries(product)) {
        if (['id', 'packageId', 'variantId', 'name'].includes(key)) continue;
        if (key === 'bundle' && Array.isArray(value)) {
          html += `<tr><td>Bundle</td><td>`;
          value.forEach((bundleItem: any, bundleIndex: number) => {
            for (const [bundleKey, bundleValue] of Object.entries(bundleItem)) {
              if (['id', 'packageId'].includes(bundleKey)) continue;
              html += `${bundleKey}: ${bundleValue}<br>`;
            }
            html += '<br>';
          });
          html += '</td></tr>';
        } else if (typeof value === 'object' && value !== null) {
          const val: any = value;
          html += `<tr><td style="text-transform:capitalize">${key}</td><td>${value ? `Subscribed ${val.frequencyName}${val.startDate ? ` from ${val.startDate}` : ''}` : 'Non-applicable'}</td></tr>`;
          for (const [subKey, subValue] of Object.entries(value)) {
            html += `${subKey}: ${subValue}<br>`;
          }
          html += '</td></tr>';
        } else {
          html += `<tr><td style="text-transform:capitalize">${key}</td><td>${value || (key === 'subscription' ? 'Non-applicable' : '')}</td></tr>`;
        }
      }
    });
    // Close the HTML table
    html += '</table>';

    return html;
  }

  /**
 * Generates product as an HTML table from the given JSON object for log registration.
 *
 * @param {Object} jsonData - The JSON object containing the bundle and product details.
 * @returns {string} - The generated HTML table as a string.
 */
  private generateProductHtmlTableFromJson(jsonData: any): string {
    const { updatedAt, bundle, category, id, img, isASubscription, isFromCard, name, package: pkg, presentation, price, productUrl, quantity, package: pack, subCategory, subscription, taxes, totalPrice, variant } = jsonData;

    // Start building the HTML table
    let html = '<table class="table table-bordered o_table">';

    // Add general details
    html += '<tr><th colspan="2" class="text-center" style="font-weight: bolder">General Details</th></tr>';
    html += `<tr><td>Product Name</td><td>${name}</td></tr>`;
    html += `<tr><td>Quantity</td><td>${quantity}</td></tr>`;
    html += `<tr><td>Package</td><td>${pack && pack.name && pack.quantity ? `${pack.name} - ${pack.quantity}` : ''}</td></tr>`;
    html += `<tr><td style="text-transform:capitalize">Subscription</td><td>${subscription ? `Subscribed ${subscription.frequencyName ?? subscription.frequency.name}${subscription.startDate ? ` from ${subscription.startDate}` : ''}` : 'Non-applicable'}</td></tr>`;

    // Add bundle items
    if (bundle?.items?.length) {
      html += '<tr><th colspan="2" class="text-center" style="font-weight: bolder">Bundle Items</th></tr>';
      bundle.items.forEach((item: any, index: number) => {
        html += `<tr><td style="text-transform:capitalize">${item.name}</td><td>${item.quantity}</td></tr>`;
      });
    }

    // Add variant details
    html += '<tr><th colspan="2" class="text-center" style="font-weight: bolder">Variant Details</th></tr>';
    for (const [key, value] of Object.entries(variant)) {
      if (!(['attribute'].includes(key))) continue;
      const val: any = value;
      html += `<tr><td style="text-transform:capitalize">${key}</td><td>${val ? `${val.name}: ${val.value.name}` : ''}</td></tr>`;
    }

    // Close the HTML table
    html += '</table>';

    return html;
  }

  private updateOrderResponseHandler(response: ApiResponse<any>, showDefaultMessage: boolean = true) {

    if (response.data.order) {
      this.odooOrder.update(() => response.data.order);
    } else {
      this.odooOrder.update(() => response.data);
    }

    if (response?.data?.previousOrder?.isLockedByCutOffDate)
      return this.openModalPastCutOff();

    const hasOutOfStockProducts = this.validateOutOfStockResponse(this.odooOrder()?.outOfStockProducts);

    const hasUnavailableProducts = this.#validateUnavailableProducts(this.odooOrder()?.notAvailableProducts);

    if (showDefaultMessage && !hasOutOfStockProducts && !hasUnavailableProducts)
      this.notificationService.show({ text: 'Order updated successfully', type: 'success' });

    if (response.data?.hasCouponError)
      return this.#enableCouponErrorMessage(response.message);
  }

  private setUpOrderProducts(product: Product, isSubscription: boolean): ProductOrderToSave {

    let productBundle = null;

    if (product.bundle && product.bundle.items.length) {
      productBundle = product.bundle.items
        .filter(bundleProdut => !bundleProdut.isRemoved)
        .map(bundleProduct => ({
          id: bundleProduct.id,
          quantity: +bundleProduct.quantity,
          packageId: bundleProduct.packageId || null
        }));
    }

    let productSubscription = null;

    if (isSubscription && product.subscription) {
      productSubscription = {
        startDate: product?.subscription?.startDate || null,
        frequencyId: product?.subscription?.frequency?.id
      };
    }

    return {
      bundle: productBundle,
      id: product.id,
      quantity: product.quantity ? +product.quantity : 0,
      packageId: product.packageId ?? null,
      subscription: productSubscription,
      variantId: product.variantId || null,
      bundleQuantityChanged: product.bundleQuantityChanged,
    };
  }

  private validateOutOfStockResponse(products: { name: string, variantName: string, productExists: boolean, previousQuantity: number }[], error: boolean = false) {
    if (!products?.length) return false;
    const outOfStock = products.map(p => {
      return {
        productName: p.name,
        productVariant: p.variantName,
        outOfStock: !p.productExists,
        limitExceeded: p.productExists,
        previousQuantity: p.previousQuantity || 0
      };
    });

    const title =
      error
        ? 'Order Below Minimum Requirement'
        : 'Update To Product Availability';
    const textContent =
      error
        ? 'Hi, we noticed that your order no longer meets the minimum required for delivery due to insufficient inventory for some items in your cart. Please review the table below for details on the changes and adjust your order accordingly. Once updated, feel free to try again. Thank you for understanding!'
        : `Hi, unfortunately, we don’t have enough inventory for the product below, so it couldn't be added to your cart`;

    this.modalContentService.openModal(ModalContentTypes.BUY_AGAIN_WARNING, {
      title,
      textContent,
      orderHistoryWarning: { products: outOfStock, showThirdColumn: true, fromOrder: true }
    })
    return true;
  }

  #validateUnavailableProducts(products: { name: string, variantName: string, productExists: boolean }[]) {
    if (!products?.length) return false;
    const unavailable = products.map(p => {
      return {
        productName: p.name,
        productVariant: p.variantName,
        outOfStock: true,
        limitExceeded: false
      };
    });

    this.modalContentService.openModal(ModalContentTypes.BUY_AGAIN_WARNING, {
      title: `Update To Product Availability`,
      textContent: `Hi, unfortunately, the following product(s) are no longer available for purchase. Please review the table below to see what we've updated.`,
      orderHistoryWarning: { products: unavailable, showThirdColumn: true, fromOrder: true }
    })
    return true;
  }

  private getFirebaseOrder(): any {
    let products = { common: [], subscription: [] }
    let paymentDetails = { tip: null, donation: null }

    if (this.notSavedOrder()) {
      products = this.notSavedOrder().products ?? null
      paymentDetails = this.notSavedOrder().paymentDetails ?? null
    }

    const orderProducts = {
      paymentDetails,
      products
    }

    return orderProducts;
  }

  addProductToFirebaseOrder(productFromCard: any, showSucceddMessage: boolean = false, overwriteOdooQuantity: boolean = false) {
    if (!productFromCard)
      throw new Error(`ERROR: product(${productFromCard}) not received`);

    const sessionStored = this.getSession();
    const productKeyType = productFromCard.isASubscription ? 'subscription' : 'common';
    // Retrieves orders from Odoo and Firebase
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.getFirebaseOrder();
    const mapKey = productFromCard.variant.id;

    // Updates the product quantity in Firebase order if it exists
    if (!this.updateProductFromFirebase(firebaseOrder, productKeyType, mapKey, productFromCard) &&
      !this.updateProductFromOdoo(odooOrder, firebaseOrder, productKeyType, mapKey, productFromCard, overwriteOdooQuantity)) {
      // If product is not found in existing orders, add it to Firebase order
      firebaseOrder.products[productKeyType].push(productFromCard);
      // ... and track the klaviyo and Navego log event.
      this.klaviyoService.trackEvent(KLAVIYOEVENTS.ProductAddedToCart, this.setupProductForKlaviyo(productFromCard));
      this.trackAddedOrDeletedProductLog(productFromCard).subscribe();
    }

    this.saveOrUpdateFirebaseOrder(firebaseOrder, productKeyType, sessionStored);
    if (showSucceddMessage)
      this.notificationService.show({ text: 'Product added successfully', type: 'success' });
  }

  getSession(): Session | null {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session || !Object.keys(session).length) {
      throw new Error("ERROR: SESSION NOT FOUNDED");
    }
    return session;
  }

  updateProductFromFirebase(firebaseOrder: any, productKeyType: string, mapKey: number | string, productFromCard: any) {

    const firebaseProductsMap = arrayToMapMultiKey(['variant.id', 'package.id'], firebaseOrder.products[productKeyType]);
    // If the product has packages, let's search by package id:
    if (productFromCard.package?.id)
      mapKey = `${mapKey}_${productFromCard.package?.id}`
    if (firebaseProductsMap && firebaseProductsMap.has(mapKey)) {
      const orderProduct = firebaseProductsMap.get(mapKey);
      orderProduct.quantity += productFromCard.quantity;
      orderProduct.subscription = productFromCard.subscription;
      firebaseOrder.products[productKeyType] = mapToArray(firebaseProductsMap);
      return true;
    }


    return false;
  }

  updateProductFromOdoo(odooOrder: any, firebaseOrder: any, productKeyType: string, mapKey: number | string, productFromCard: any, overWriteOdooQuantity: boolean = false) {

    if (odooOrder?.products?.[productKeyType]) {
      const odooProductsMap = arrayToMapMultiKey(['variant.id', 'package.id'], odooOrder.products[productKeyType]);
      // If the product has packages, let's search by package id:
      if (productFromCard.package?.id)
        mapKey = `${mapKey}_${productFromCard.package?.id}`
      if (odooProductsMap && odooProductsMap.has(mapKey)) {
        const orderProduct = JSON.parse(JSON.stringify(odooProductsMap.get(mapKey)));
        if (overWriteOdooQuantity)
          orderProduct.quantity = productFromCard.quantity;
        else
          orderProduct.quantity += productFromCard.quantity;
        firebaseOrder.products[productKeyType].push(orderProduct);
        return true;
      }
    }
    return false;
  }

  saveOrUpdateFirebaseOrder(firebaseOrder: any, productKeyType: string, sessionStored: any) {
    if (!this.notSavedOrder()) {
      let updateData: any = { orderProducts: firebaseOrder };
      // Setup cutoff date:
      updateData = { ...updateData, ...this.setUpDateForFirebase(), eventCreated: false }
      this.firebaseCrudService.add(
        FIREBASE_COLLECTIONS.ORDERS,
        sessionStored.accountInfo.id,
        updateData
      );
    } else {
      const payload = {
        subKeyPath: `orderProducts.products.${productKeyType}`,
        newValue: firebaseOrder.products[productKeyType]
      };
      this.editSubKey(payload);
    }
  }

  deleteProductOrder(orderId: number, lineId: number) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order + `/${orderId}/line/${lineId}`,
      method: 'DELETE',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        if (response?.data?.previousOrder?.isLockedByCutOffDate)
          return this.openModalPastCutOff()

        this.notificationService.show({ text: 'Product removed from order successfully', type: 'success' });
        this.odooOrder.set(response?.data?.order);
      }),
    )
  }

  applyCouponWithOrder(coupon: WritableSignal<string>, orderId: number) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    this.#disableCouponErrorMessage();
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const cleanedCoupon = coupon().trim();
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.coupon,
      method: 'POST',
      body: { coupon: cleanedCoupon, orderId },
      apiV3: true,
      returnError: true,
      showErrorMessage: false
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      filter(response => {
        if (!response.error) return true;
        this.#enableCouponErrorMessage(response.message);
        return false;
      }),
      tap((response: any) => {
        coupon.set('');
        this.notificationService.show({ text: response?.message, type: 'success' });
        this.odooOrder.set(response?.data);
      }),
    )
  }

  skipOrder(args: {
    orderId: number;
    isSkipping: boolean;
    donationAmount: number | any
  }) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const { orderId, isSkipping, donationAmount } = args;
    const donation = !donationAmount ? null : {
      amount: donationAmount,
      isRecurrent: false,
    }
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order + `/${orderId}/skip`,
      method: 'POST',
      body: {
        isSkipping,
        donation
      },
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        if (response?.data?.previousOrder?.isLockedByCutOffDate)
          return this.openModalPastCutOff()

        const payload = {
          subKeyPath: `orderProducts`,
          newValue: null
        }
        const actionPhrase = isSkipping ? 'applied' : 'removed';
        this.editSubKey(payload, true, true);
        this.notificationService.show({ text: `Order skip ${actionPhrase} successfully`, type: 'success' });
        this.odooOrder.set(response?.data?.order);
      }),

      tap(() => (this.deliveriesService.getDeliveryZoneInfo().subscribe())),
    )
  }

  private openModalPastCutOff() {
    this.modalContentService.openModal(ModalContentTypes.PAST_CUT_OFF, { closeable: true })
      .closed
      .subscribe((res) => {
        if (res.understoodClicked) {
          const payload = {
            subKeyPath: `orderProducts`,
            newValue: null
          }
          this.editSubKey(payload, true, true);
          window.location.reload()
        }
      })
  }

  private updateProductInFirebase(product: any, productList: any[]): any[] {
    let isProductInList: boolean = false;
    const newProductList = productList.map((productItem) => {
      if (productItem.id === product.id) {
        isProductInList = true;
        return product;
      }
      return productItem;
    });

    if (!isProductInList)
      newProductList.push(product)

    return newProductList
  }

  updateFirebaseProduct(productKey: string, product: any, firebaseOrder: any) {
    const firebaseProductsOrder = firebaseOrder?.products?.[productKey] ?? [];
    const newValue = this.updateProductInFirebase(product, firebaseProductsOrder);
    const payload = {
      subKeyPath: `orderProducts.products.${productKey}`,
      newValue
    }

    this.editSubKey(payload);
  }

  removeFirebaseProduct(productKey: string, product: any, firebaseOrder: any) {
    const firebaseProductsOrder = firebaseOrder?.products?.[productKey] || [];
    if (!firebaseProductsOrder?.length)
      return

    const newValue = this.removeProductFromArray(product, firebaseProductsOrder);
    const payload = {
      subKeyPath: `orderProducts.products.${productKey}`,
      newValue
    }

    this.editSubKey(payload);
    this.trackAddedOrDeletedProductLog(product, true).subscribe();
  }

  trackAddedOrDeletedProductLog(product: any, removed: boolean = false) {
    const content = this.generateProductHtmlTableFromJson(product);
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.ecommerceLogs,
      method: 'POST',
      apiV3: true,
      body: {
        name: removed ? 'Product Deleted (from unsubmitted order)' : 'Product Added to Order (not yet saved)',
        content,
        orderId: this.odooOrder()?.id || null
      },
      showErrorMessage: false
    };

    return this.apiService.handleRequest(params);
  }

  private removeProductFromArray(product: any, productList: any[]): any[] {
    return productList.filter((productItem) => productItem.package?.id && product.package?.id ? product.package.id !== productItem.package.id : productItem.variant.id !== product.variant.id);
  }

  checkIfExistsProductInFirebaseOrder(product: any, firebaseOrder: any, bundleEditionType: BundleEditionType) {
    const mapKey = product?.variant?.id;
    if (!firebaseOrder || !firebaseOrder?.products?.[bundleEditionType])
      return null

    const firebaseProductsMap = arrayToMap('variant.id', firebaseOrder.products[bundleEditionType]);
    if (firebaseProductsMap.has(mapKey))
      return firebaseProductsMap.get(mapKey);

    return null;
  }

  addBundleToFirebaseOrder(firebaseOrder: any, productFromCard: any, bundleEditionType: BundleEditionType) {
    if (!productFromCard)
      return throwError(new Error(`ERROR: product(${productFromCard}) not received`));

    const mapKey = productFromCard.variant.id;
    const updatedFirebaseOrder = this.updateBundleFromFirebase(firebaseOrder, mapKey, productFromCard, bundleEditionType)

    const sessionStored = this.getSession();
    const productKeyType = productFromCard?.isASubscription ? 'subscription' : 'common';
    const res = this.saveOrUpdateFirebaseOrder2(updatedFirebaseOrder, productKeyType, sessionStored);

    return res;
  }

  updateBundleFromFirebase(firebaseOrder: any, mapKey: number, product: any, bundleEditionType: BundleEditionType) {
    // const productKeyType = product?.isSubscription || product?.isASubscription ? 'subscription' : 'common';

    if (!firebaseOrder)
      firebaseOrder = {}

    if (!firebaseOrder?.products)
      firebaseOrder['products'] = {};

    if (!Array.isArray(firebaseOrder.products?.[bundleEditionType]))
      firebaseOrder.products[bundleEditionType] = [];

    const firebaseProductsMap = arrayToMap('variant.id', firebaseOrder.products[bundleEditionType]);

    if (firebaseProductsMap?.has(mapKey)) {
      firebaseProductsMap.set(mapKey, product);
      firebaseOrder.products[bundleEditionType] = mapToArray(firebaseProductsMap);
    } else {
      firebaseOrder.products[bundleEditionType].push(product);
      this.klaviyoService.trackEvent(KLAVIYOEVENTS.ProductAddedToCart, this.setupProductForKlaviyo(product));
      this.trackAddedOrDeletedProductLog(product).subscribe();
    }

    return firebaseOrder;
  }

  saveOrUpdateFirebaseOrder2(firebaseOrder: any, productKeyType: string, sessionStored: any) {
    let response: any;
    if (!this.notSavedOrder()) {
      let updateData: any = { orderProducts: firebaseOrder }
      // Setup cutoff date:
      updateData = { ...updateData, ...this.setUpDateForFirebase(), eventCreated: false }
      response = this.firebaseCrudService.add(
        FIREBASE_COLLECTIONS.ORDERS,
        sessionStored.accountInfo.id,
        updateData
      )
    } else {
      const payload = {
        subKeyPath: `orderProducts.products.${productKeyType}`,
        newValue: firebaseOrder.products[productKeyType]
      };
      response = this.editSubKey(payload, true, false, false);
    }

    return from(response)
  }

  setupProductForKlaviyo(product: any) {
    return {
      bundle: product.bundle?.items || [],
      category: product.category,
      id: product.id,
      name: product.name,
      price: product.price,
      productUrl: product.productUrl,
      size: product.size || '',
      subcategory: product.subCategory,
      originalPrice: product.originalPrice,
      producer: product.producer,
      specialCategory: product.specialCategory,
      tags: product.tags,
      variant: product.variant,
      package: product.package
    }
  }

  savePreOrderProduct(preOrderData: PreOrderPayloadProduct) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}${this.endpoints.preOrder}`,
      method: 'POST',
      apiV3: true,
      body: preOrderData
    };

    return this.apiService.handleRequest(params);
  }

  getPreOrders() {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}${this.endpoints.preOrder}`,
      method: 'GET',
      apiV3: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<PreOrderedProduct[]>>(params).pipe(
      filter((data) => !!data.data.length),
      map((data) => {
        const res = data.data.map(p => {
          p.preOrder = this.#setUpPreorderProductData(p.preOrder, p.quantity);
          return p;
        })

        return res;
      })
    )
  }

  #setUpPreorderProductData(preOrder: PreOrderData | null, quantity: number): PreOrderData | null {
    if (!preOrder) return null;
    return {
      ...preOrder,
      _readableEndDate: formatDateToReadableString(preOrder.endDate),
      _readableStartDeliveryDate: formatDateToReadableString(preOrder.startDeliveryDate),
      _readableEndDeliveryDate: formatDateToReadableString(preOrder.endDeliveryDate),
      deposit: preOrder.deposit * quantity
    } as PreOrderData
  }

  cancelOrderById(orderId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}/${orderId}`,
      method: 'DELETE',
      apiV3: true,
      isVoid: true
    };

    return this.apiService.handleRequest<void>(params).pipe(
      tap(() => this.getOrder()),
      tap(() => this.deliveriesService.getDeliveryZoneInfo().subscribe()),
      tap(() => this.notificationService.show({ text: 'Your order has been successfully cancelled', type: 'success' }))
    );
  }

  #enableCouponErrorMessage(message?: string) {
    this.showCouponErrorMessage.set(true);
    this.couponErrorMessage.set(message || 'There was an error trying to apply the coupon');
    setTimeout(() => {
      this.#disableCouponErrorMessage()
    }, 10 * 1000);
  }

  #disableCouponErrorMessage() {
    this.showCouponErrorMessage.set(false);
    this.couponErrorMessage.set('');
  }

  calculateProductTotalPrice(product: any): number {
    let totalPrice = 0;
    if (product?.bundle?.items) {
      if (product.bundle.isFixed) {
        totalPrice = +product.price;
        const premiumItems = product?.bundle?.items?.filter((pi: any) => pi.isPremiumAddon);
        if (premiumItems?.length) {
          totalPrice += premiumItems.reduce((total: number, bundleItem: any) => {
            const bundleItemPrice = (+bundleItem?.price * +bundleItem?.quantity) || 0
            return total + bundleItemPrice
          }, 0);
        }
      } else if (product?.bundle?.items?.length)
        totalPrice = product.bundle.items.reduce((total: number, bundleItem: any) => {
          const bundleItemPrice = (bundleItem?.isRemoved ? 0 : (+bundleItem?.price * +bundleItem?.quantity) || 0)
          return total + bundleItemPrice
        }, 0);
    }
    if (totalPrice === 0)
      totalPrice = product.price

    return +((totalPrice * product.quantity).toFixed(2));
  }

}
